import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { authApi, paymentsInfrastructureApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import {
    getMerchantAccountDisplayLabel,
    getLogicalTerminalDisplayLabel,
    getTerminalDeviceDisplayLabel,
} from '@/lib/payments-infrastructure/displayLabels';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type {
    ActiveRoleAssignmentOperationalResponse,
    ActiveRoleAssignmentResponse,
    BusinessDetails,
    LogicalTerminalListItem,
    LogicalTerminalSearchPageResponse,
    MerchantAccountSearchListItem,
    MerchantAccountSearchPageResponse,
    ProcessorListItem,
    ProcessorSearchPageResponse,
    TerminalDeviceListItem,
    TerminalDeviceSearchPageResponse,
} from '@pillar-portal/api-types';
import { hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

function getMerchantDisplayName(businessDetails: BusinessDetails | undefined): string | null {
    const dba = businessDetails?.dba?.trim();
    if (dba !== undefined && dba.length > 0) {
        return dba;
    }

    const legalEntityName = businessDetails?.legalEntityName.trim();
    if (legalEntityName !== undefined && legalEntityName.length > 0) {
        return legalEntityName;
    }

    return null;
}

function parseOpaqueCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = z.string().trim().min(1).safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

type MerchantAccountsListCardProps = {
    organizationUuid?: string;
    showPageHeader?: boolean;
    cardId?: string;
    compactBottomSpacing?: boolean;
    allowSubsidiariesToggle?: boolean;
};

function excludeDeletedProcessors(
    processors: ReadonlyArray<ProcessorListItem>
): ReadonlyArray<ProcessorListItem> {
    return processors.filter((processor) => processor.status !== Status.DELETED);
}

function DuplicateControls(props: {
    hasDuplicates: boolean;
    duplicatesOnly: boolean;
    onToggleDuplicatesOnly: () => void;
}): ReactElement | null {
    if (!props.hasDuplicates) {
        return null;
    }
    return (
        <div className="mb-3 flex items-center justify-between rounded border border-red-300 bg-red-50 p-3">
            <p className="text-sm text-red-700">
                Duplicate IDs detected in your current access scope.
            </p>
            <Button variant="destructive" size="sm" onClick={props.onToggleDuplicatesOnly}>
                {props.duplicatesOnly ? 'Show all records' : 'Show duplicates only'}
            </Button>
        </div>
    );
}

function useActiveRoleAssignment(): {
    activeRoleAssignmentOperationalResponse: ActiveRoleAssignmentOperationalResponse | null;
    isLoading: boolean;
} {
    const queryResult = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    return {
        activeRoleAssignmentOperationalResponse: getActiveRoleAssignmentOperationalResponse(
            queryResult.data
        ),
        isLoading: queryResult.isLoading,
    };
}

export function ProcessorsListPage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'processors',
            `orgs=${organizationListsVersion}`,
            `showDeleted=${showDeleted}`,
        ],
        [organizationListsVersion, showDeleted]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignmentOperationalResponse
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<ProcessorListItem, string, ReadonlyArray<string>>
            pageTitle="Processors"
            cardTitle="Processors"
            queryPlaceholder="Search processors..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: ProcessorSearchPageResponse =
                    await paymentsInfrastructureApi.searchProcessors({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        show_deleted: showDeleted ? true : undefined,
                    });
                return {
                    items: showDeleted
                        ? response.processors.filter(
                              (processor) => processor.status === Status.DELETED
                          )
                        : excludeDeletedProcessors(response.processors),
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading processors...',
                emptyLabel: 'No processors found.',
                endOfListLabel: 'End of processors.',
                genericErrorLabel: 'Failed to load processors.',
            }}
            headerActions={
                canCreate ? (
                    <Button
                        id="create-processor-button"
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/payments-infrastructure/processors/create',
                                    title: 'Create Processor',
                                },
                            });
                        }}
                    >
                        Create Processor
                    </Button>
                ) : undefined
            }
            controls={
                canCreate ? (
                    <div className="flex items-center gap-2">
                        <Label htmlFor="processor-show-deleted" className="cursor-pointer">
                            Show deleted
                        </Label>
                        <Checkbox
                            id="processor-show-deleted"
                            checked={showDeleted}
                            onCheckedChange={(checked) => {
                                setShowDeleted(checked === true);
                            }}
                        />
                    </div>
                ) : undefined
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((processor: ProcessorListItem) => (
                        <button
                            key={processor.processor_name}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: `/payments-infrastructure/processors/${encodeURIComponent(processor.processor_name)}`,
                                        title: 'Processor Details',
                                    },
                                });
                            }}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="font-semibold">{processor.processor_name}</h3>
                                <StatusBadge status={processor.status} />
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function MerchantAccountsListCard(props: MerchantAccountsListCardProps): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const [includeDescendants, setIncludeDescendants] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'merchantAccounts',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
            `organization=${props.organizationUuid ?? 'all'}`,
            `descendants=${includeDescendants}`,
            `showDeleted=${showDeleted}`,
        ],
        [
            duplicatesOnly,
            includeDescendants,
            organizationListsVersion,
            props.organizationUuid,
            showDeleted,
        ]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignmentOperationalResponse
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<MerchantAccountSearchListItem, string, ReadonlyArray<string>>
            pageTitle="Merchant Accounts"
            cardTitle="Merchant Accounts"
            cardId={props.cardId}
            showPageHeader={props.showPageHeader}
            compactBottomSpacing={props.compactBottomSpacing}
            queryPlaceholder="Search merchant accounts..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: MerchantAccountSearchPageResponse =
                    await paymentsInfrastructureApi.searchMerchantAccounts({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                        organization_uuid: props.organizationUuid,
                        include_descendants:
                            props.organizationUuid !== undefined ? includeDescendants : undefined,
                        show_deleted: showDeleted ? true : undefined,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.merchant_accounts, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading merchant accounts...',
                emptyLabel: 'No merchant accounts found.',
                endOfListLabel: 'End of merchant accounts.',
                genericErrorLabel: 'Failed to load merchant accounts.',
            }}
            cardHeaderActions={
                <div className="flex items-center gap-3">
                    {props.allowSubsidiariesToggle === true ? (
                        <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                                checked={includeDescendants}
                                onCheckedChange={(checked) => {
                                    setIncludeDescendants(checked === true);
                                }}
                            />
                            <span>Show subsidiaries</span>
                        </label>
                    ) : null}
                    {canCreate ? (
                        <Button
                            id="create-merchant-account-button"
                            onClick={() => {
                                const createMerchantAccountUrl: string =
                                    props.organizationUuid === undefined
                                        ? '/payments-infrastructure/merchant-accounts/create'
                                        : `/payments-infrastructure/merchant-accounts/create?organization_uuid=${encodeURIComponent(
                                              props.organizationUuid
                                          )}`;
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: createMerchantAccountUrl,
                                        title: 'Create Merchant Account',
                                    },
                                });
                            }}
                        >
                            Create Merchant Account
                        </Button>
                    ) : null}
                </div>
            }
            controls={
                <>
                    <DuplicateControls
                        hasDuplicates={hasDuplicatesInScope}
                        duplicatesOnly={duplicatesOnly}
                        onToggleDuplicatesOnly={() => {
                            setDuplicatesOnly((currentValue: boolean) => !currentValue);
                        }}
                    />
                    <div className="flex items-center gap-2">
                        <Label htmlFor="merchant-account-show-deleted" className="cursor-pointer">
                            Show deleted
                        </Label>
                        <Checkbox
                            id="merchant-account-show-deleted"
                            checked={showDeleted}
                            onCheckedChange={(checked) => {
                                setShowDeleted(checked === true);
                            }}
                        />
                    </div>
                </>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((merchantAccount: MerchantAccountSearchListItem) => (
                        <button
                            key={merchantAccount.merchant_account_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: `/payments-infrastructure/merchant-accounts/${merchantAccount.merchant_account_uuid}`,
                                        title: 'Merchant Account Details',
                                    },
                                });
                            }}
                        >
                            {getMerchantDisplayName(merchantAccount.businessDetails) !== null && (
                                <p className="text-sm text-muted-foreground">
                                    {getMerchantDisplayName(merchantAccount.businessDetails)}
                                </p>
                            )}
                            <div className="flex items-center justify-between gap-3">
                                <h3 className="font-semibold">
                                    {getMerchantAccountDisplayLabel(merchantAccount)}
                                </h3>
                                <StatusBadge status={merchantAccount.status} />
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function MerchantAccountsListPage() {
    return <MerchantAccountsListCard cardId="merchant-accounts-list-card" />;
}

export function LogicalTerminalsListPage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'logicalTerminals',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
            `showDeleted=${showDeleted}`,
        ],
        [duplicatesOnly, organizationListsVersion, showDeleted]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignmentOperationalResponse
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<LogicalTerminalListItem, string, ReadonlyArray<string>>
            pageTitle="Logical Terminals"
            cardTitle="Logical Terminals"
            queryPlaceholder="Search logical terminals..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: LogicalTerminalSearchPageResponse =
                    await paymentsInfrastructureApi.searchLogicalTerminals({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                        show_deleted: showDeleted ? true : undefined,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.logical_terminals, pagination: response.pagination };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading logical terminals...',
                emptyLabel: 'No logical terminals found.',
                endOfListLabel: 'End of logical terminals.',
                genericErrorLabel: 'Failed to load logical terminals.',
            }}
            headerActions={
                canCreate ? (
                    <Button
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/payments-infrastructure/logical-terminals/create',
                                    title: 'Create Logical Terminal',
                                },
                            });
                        }}
                    >
                        Create Logical Terminal
                    </Button>
                ) : undefined
            }
            controls={
                <>
                    <DuplicateControls
                        hasDuplicates={hasDuplicatesInScope}
                        duplicatesOnly={duplicatesOnly}
                        onToggleDuplicatesOnly={() => {
                            setDuplicatesOnly((currentValue: boolean) => !currentValue);
                        }}
                    />
                    <div className="flex items-center gap-2">
                        <Label htmlFor="logical-terminal-show-deleted" className="cursor-pointer">
                            Show deleted
                        </Label>
                        <Checkbox
                            id="logical-terminal-show-deleted"
                            checked={showDeleted}
                            onCheckedChange={(checked) => {
                                setShowDeleted(checked === true);
                            }}
                        />
                    </div>
                </>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((logicalTerminal: LogicalTerminalListItem) => (
                        <button
                            key={logicalTerminal.logical_terminal_uuid}
                            data-logical-terminal-uuid={logicalTerminal.logical_terminal_uuid}
                            data-logical-terminal-system-id={logicalTerminal.ipg_system_id}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: `/payments-infrastructure/logical-terminals/${logicalTerminal.logical_terminal_uuid}`,
                                        title: 'Logical Terminal Details',
                                    },
                                });
                            }}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    {getMerchantDisplayName(logicalTerminal.businessDetails) !==
                                        null && (
                                        <p className="text-sm text-muted-foreground">
                                            {getMerchantDisplayName(
                                                logicalTerminal.businessDetails
                                            )}
                                        </p>
                                    )}
                                    <h3 className="font-semibold">
                                        {getLogicalTerminalDisplayLabel(logicalTerminal)}
                                    </h3>
                                </div>
                                <StatusBadge status={logicalTerminal.status} />
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export function TerminalDevicesListPage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const organizationListsVersion: number = useDataInvalidationStore(
        (state) => state.organizationListsVersion
    );
    const roleAssignmentQuery = useActiveRoleAssignment();
    const [duplicatesOnly, setDuplicatesOnly] = useState<boolean>(false);
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [hasDuplicatesInScope, setHasDuplicatesInScope] = useState<boolean>(false);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'paymentsInfrastructure',
            'terminalDevices',
            `orgs=${organizationListsVersion}`,
            `duplicates=${duplicatesOnly}`,
            `showDeleted=${showDeleted}`,
        ],
        [duplicatesOnly, organizationListsVersion, showDeleted]
    );
    const canCreate: boolean = hasInternalAdminPrivileges(
        roleAssignmentQuery.activeRoleAssignmentOperationalResponse
    );

    if (roleAssignmentQuery.isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<TerminalDeviceListItem, string, ReadonlyArray<string>>
            pageTitle="Terminal Devices"
            cardTitle="Terminal Devices"
            queryPlaceholder="Search terminal devices..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response: TerminalDeviceSearchPageResponse =
                    await paymentsInfrastructureApi.searchTerminalDevices({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        duplicates_only: duplicatesOnly,
                        show_deleted: showDeleted ? true : undefined,
                    });
                setHasDuplicatesInScope(response.duplicate_summary.has_duplicates);
                if (!response.duplicate_summary.has_duplicates && duplicatesOnly) {
                    setDuplicatesOnly(false);
                }
                return { items: response.terminal_devices, pagination: response.pagination };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading terminal devices...',
                emptyLabel: 'No terminal devices found.',
                endOfListLabel: 'End of terminal devices.',
                genericErrorLabel: 'Failed to load terminal devices.',
            }}
            headerActions={
                canCreate ? (
                    <Button
                        onClick={() => {
                            breadcrumbNavigate({
                                source: 'panel',
                                target: {
                                    url: '/payments-infrastructure/terminal-devices/create',
                                    title: 'Create Terminal Device',
                                },
                            });
                        }}
                    >
                        Create Terminal Device
                    </Button>
                ) : undefined
            }
            controls={
                <>
                    <DuplicateControls
                        hasDuplicates={hasDuplicatesInScope}
                        duplicatesOnly={duplicatesOnly}
                        onToggleDuplicatesOnly={() => {
                            setDuplicatesOnly((currentValue: boolean) => !currentValue);
                        }}
                    />
                    <div className="flex items-center gap-2">
                        <Label htmlFor="terminal-device-show-deleted" className="cursor-pointer">
                            Show deleted
                        </Label>
                        <Checkbox
                            id="terminal-device-show-deleted"
                            checked={showDeleted}
                            onCheckedChange={(checked) => {
                                setShowDeleted(checked === true);
                            }}
                        />
                    </div>
                </>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((terminalDevice: TerminalDeviceListItem) => (
                        <button
                            key={terminalDevice.terminal_device_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => {
                                breadcrumbNavigate({
                                    source: 'panel',
                                    target: {
                                        url: `/payments-infrastructure/terminal-devices/${terminalDevice.terminal_device_uuid}`,
                                        title: 'Terminal Device Details',
                                    },
                                });
                            }}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    {getMerchantDisplayName(terminalDevice.businessDetails) !==
                                        null && (
                                        <p className="text-sm text-muted-foreground">
                                            {getMerchantDisplayName(terminalDevice.businessDetails)}
                                        </p>
                                    )}
                                    <h3 className="font-semibold">
                                        {getTerminalDeviceDisplayLabel(terminalDevice)}
                                    </h3>
                                    <div className="mt-1">
                                        <span
                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                                terminalDevice.is_virtual
                                                    ? 'bg-gray-100 text-gray-700'
                                                    : 'bg-blue-100 text-blue-700'
                                            }`}
                                        >
                                            {terminalDevice.is_virtual ? 'Virtual' : 'Physical'}
                                        </span>
                                    </div>
                                </div>
                                <StatusBadge status={terminalDevice.status} />
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}
